import { Component } from "@theme/component";

/**
 * A lightweight product carousel for the "Shop the look" home section.
 *
 * Uses native CSS scroll-snap for sizing/paging (no Swiper), so it renders
 * reliably even inside the slide panels that start hidden. Prev/next arrows and
 * the pagination dots are progressive enhancements.
 *
 * Emits `shop-the-look:productchange` (bubbling) with the active product handle
 * so the parent can highlight the matching image hotspot, and exposes
 * `scrollToHandle` so a hotspot click can bring its product into view.
 *
 * Refs:
 * - track: the horizontal scroll container holding the product items
 * - prev, next: arrow buttons (optional)
 * - dots: container with one bullet button per product (optional)
 */
class ShopTheLookProducts extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement[]} */
  #items = [];

  #currentIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;
    if (!track) return;

    this.#items = /** @type {HTMLElement[]} */ ([...track.children]);
    if (this.#items.length === 0) return;

    if (this.#items.length > 1) {
      // Reflect the scrolled-to item in the counter, arrows and linked hotspot.
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            this.#setActive(this.#items.indexOf(/** @type {HTMLElement} */ (entry.target)));
          }
        },
        { root: track, threshold: 0.6 }
      );

      for (const item of this.#items) this.#observer.observe(item);
    }

    this.#setActive(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  next() {
    this.#scrollToIndex(this.#currentIndex + 1);
  }

  previous() {
    this.#scrollToIndex(this.#currentIndex - 1);
  }

  /**
   * @param {Event} event
   */
  goToDot(event) {
    const target = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest("[data-index]"));
    if (!target) return;
    const index = Number(target.dataset.index);
    if (!Number.isNaN(index)) this.#scrollToIndex(index);
  }

  /**
   * Bring the product with the given handle into view (used when a hotspot on
   * the image is clicked).
   * @param {string} handle
   */
  scrollToHandle(handle) {
    if (!handle) return;
    const index = this.#items.findIndex((item) => item.dataset.handle === handle);
    if (index !== -1) this.#scrollToIndex(index);
  }

  /**
   * @param {number} index
   */
  #scrollToIndex(index) {
    const track = /** @type {HTMLElement | undefined} */ (this.refs.track);
    const clamped = Math.max(0, Math.min(index, this.#items.length - 1));
    const item = this.#items[clamped];
    if (track && item) {
      track.scrollTo({ left: item.offsetLeft, behavior: "smooth" });
    }
  }

  /**
   * @param {number} index
   */
  #setActive(index) {
    this.#currentIndex = index;

    const dots = /** @type {HTMLElement | undefined} */ (this.refs.dots);
    if (dots) {
      for (const bullet of dots.querySelectorAll("[data-index]")) {
        const active = Number(/** @type {HTMLElement} */ (bullet).dataset.index) === index;
        bullet.classList.toggle("swiper-pagination-bullet-active", active);
        bullet.setAttribute("aria-current", active ? "true" : "false");
      }
    }

    this.#updateControls();

    const handle = this.#items[index]?.dataset.handle;
    if (handle) {
      this.dispatchEvent(
        new CustomEvent("shop-the-look:productchange", { bubbles: true, detail: { handle } })
      );
    }
  }

  #updateControls() {
    const prev = /** @type {HTMLButtonElement | undefined} */ (this.refs.prev);
    const next = /** @type {HTMLButtonElement | undefined} */ (this.refs.next);
    if (prev) prev.disabled = this.#currentIndex <= 0;
    if (next) next.disabled = this.#currentIndex >= this.#items.length - 1;
  }
}

if (!customElements.get("shop-the-look-products")) {
  customElements.define("shop-the-look-products", ShopTheLookProducts);
}
